import { create } from "zustand";
import { persist } from "zustand/middleware";
export type RatingType = {
  average: number;
  totalReviews: number;
  myRating: {
    _id: string;
    rating: number;
    comment?: string;
  } | null;
  comments: Array<{
    _id: string;
    accountId: string;
    comment: string;
    createdAt?: string;
    rating: number;
    accountName?: string;
  }>;

  starCounts: Array<{
    star: number;
    count: number;
  }>; 
};
export type Product = {
  _id: string;
  name: string;
  categoryId: {
    _id: string;
    slug: string;
    name: string;
  };
  price: number;
  imageUrls?: { url: string }[];
  description?: string;
  shortDescription?: string;
  clicks?: number;
  convertedAmount?: {
    currency?: string;
    amount?: number;
  };
  currency: string;
  createdAt?: string;
  updatedAt?: string;
  inventoryCount?: number;
  sold?: number;
  rating?: RatingType;
  liked?: boolean;
};

type CartItem = Product & {
  quantity: number;
  price: number;
};

interface CartStore {
  cart: CartItem[];
  addToCart: (product: Product) => void;
  removeOneFromCart: (id: string | number) => void;
  removeProduct: (id: string | number) => void;
  updateProductQuantity: (id: string, quantity: number) => void;

  clearCart: () => void;
  getTotal: () => number;
  getTotalQuantity: () => number;

  updateCartItems: (
    updatedProducts: Array<{
      productId: string;
      name: string;
      price: number;
      convertedCurrency: string;
      product_currency: string;
      product_image?: { url: string; key?: string; _id?: string };
      store_name?: string;
      store_logo?: { url: string; key?: string };
      store_id: string;
    }>,
  ) => void;
}

const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      cart: [],

      addToCart: (product: Product) => {
        set((state) => {
          const existingItem = state.cart.find(
            (item) => item._id === product._id,
          );

          if (existingItem) {
            return {
              cart: state.cart.map((item) =>
                item._id === product._id
                  ? { ...item, quantity: item.quantity + 1 }
                  : item,
              ),
            };
          }

          return {
            cart: [
              ...state.cart,
              {
                ...product,
                quantity: 1,
                price:
                  typeof product.price === "string"
                    ? parseFloat(product.price)
                    : product.price,
              },
            ],
          };
        });
      },
      removeOneFromCart: (id) => {
        set((state) => ({
          cart: state.cart
            .map((item) =>
              item._id === id ? { ...item, quantity: item.quantity - 1 } : item,
            )
            .filter((item) => item.quantity > 0),
        }));
      },

      removeProduct: (id) => {
        set((state) => ({
          cart: state.cart.filter((item) => item?._id !== id),
        }));
      },

      clearCart: () => set({ cart: [] }),
      updateProductQuantity: (id, quantity) => {
        set((state) => ({
          cart: state.cart
            .map((item) =>
              item._id === id
                ? { ...item, quantity }
                : item
            )
            .filter((item) => item.quantity > 0),
        }));
      },

      getTotal: () =>
        get().cart.reduce((acc, item) => acc + item?.price * item?.quantity, 0),
      getTotalQuantity: () =>
        get().cart.reduce((sum, item) => sum + item.quantity, 0),
      updateCartItems: (updatedProducts) => {
        set((state) => {
          const quantityMap = new Map<string, number>();
          state.cart.forEach((item) => {
            const existingQty = quantityMap.get(item?._id) || 0;
            quantityMap.set(item?._id, existingQty + item?.quantity);
          });

          const newCart: CartItem[] = updatedProducts
            .map((updatedProduct) => {
              const existingQuantity =
                quantityMap.get(updatedProduct?.productId) || 1;

              return {
                _id: updatedProduct?.productId,
                name: updatedProduct?.name,
                price: updatedProduct?.price,
                imageUrls: updatedProduct?.product_image
                  ? [{ url: updatedProduct?.product_image.url }]
                  : [],
                convertedAmount: {
                  currency: updatedProduct?.convertedCurrency,
                  amount: updatedProduct?.price,
                },
                currency: updatedProduct?.product_currency,
                quantity: existingQuantity,
                categoryId: {
                  _id: "",
                  slug: "",
                  name: "",
                },
                storeId: {
                  name: updatedProduct?.store_name,
                  logo: updatedProduct?.store_logo,
                  storeId: updatedProduct?.store_id,
                },
              } as CartItem;
            })
            .filter((item) => item !== null);

          return { cart: newCart };
        });
      },
    }),
    {
      name: "cart-storage",
    },
  ),
);

export default useCartStore;
